import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';

import Button from '../button';
import CustomSingleSelect from '../custom_single_select';
import RequiredReasonTextarea from '../required_reason_textarea';
import { trim_reason } from '../required_reason_textarea/reason_rules';
import { save_care_request } from './care_request_api';
import {
  INTERNAL_NOTE_LABEL,
  INTERNAL_NOTE_PLACEHOLDER,
  KIND_TEACHER_CHANGE,
  care_api_error,
  covers_care,
  has_errors,
  new_care_draft
} from './care_request_rules';
import {
  STAGE_UPLOAD,
  change_teacher_with_care,
  swap_error_message,
  validate_teacher_change
} from './teacher_change_flow';
import { CareRequestFields, CareSaveRetry, TeacherNoteField } from './index';
import { FormActions } from '../form_layout';
import RequestTimeWarningStep from '../request_time_warning';
import useRequestTimeWarning from '../request_time_warning/use_request_time_warning';
import { client_request_time_warning } from '../request_time_warning/request_time_warning_rules';
import { read_care_save_warning, with_care_ack } from './care_ack_rules';
import PayTimeWarningStep from '../pay_time_warning';
import usePayTimeWarning from '../pay_time_warning/use_pay_time_warning';

// The host owns the apis call (swap(teacher_uuid, note, pay_time_ack): it sends the ack with the
// swap when given) and what happens after (on_done: close + reload; on_error:
// notify).
function TeacherChangeForm(props) {
  const {
    session_uuid,
    session_type,
    teacher_options,
    initial_teacher_uuid,
    swap,
    on_done,
    on_error,
    class_start,
    booked_at
  } = props;
  const rt = useRequestTimeWarning();
  const pw = usePayTimeWarning();
  // the request-time acknowledgement, kept while a pay-time warning (from the swap) is answered
  const rt_ack = useRef(null);
  const [teacher_uuid, setTeacher] = useState(initial_teacher_uuid || '');
  const [reason, setReason] = useState('');
  const [draft, setDraft] = useState(() =>
    covers_care(KIND_TEACHER_CHANGE, session_type)
      ? new_care_draft(KIND_TEACHER_CHANGE, Date.now())
      : null
  );
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [retry, setRetry] = useState(null); // {payload, message, busy, warning}
  const screenshot_key = useRef('');
  const in_flight = useRef(false); // closes the double-click window before setState lands

  // pay_ack: the pay-time acknowledgement (apis warned on the swap, A-52); null on the first send
  const submit = async (pay_ack = null) => {
    const errs = validate_teacher_change({ teacher_uuid, reason, draft, now_ms: Date.now() });
    if (has_errors(errs)) {
      setErrors(errs);
      return;
    }
    if (in_flight.current) return;
    const pre =
      !rt_ack.current && draft
        ? client_request_time_warning({
            requested_local: draft.requested_at,
            class_start,
            booked_at
          })
        : null;
    if (pre) {
      rt.show(pre);
      return;
    }
    in_flight.current = true;
    setBusy(true);
    const note = trim_reason(reason);
    try {
      const res = await change_teacher_with_care(
        {
          session_uuid,
          teacher_uuid,
          note,
          draft,
          screenshot_key: screenshot_key.current,
          request_time_ack: rt_ack.current
        },
        { swap: () => swap(teacher_uuid, note, pay_ack) }
      );
      if (res.saved) {
        on_done();
        return;
      }
      setRetry({
        payload: res.payload,
        busy: false,
        message: care_api_error(res.error, 'Try again.').message,
        warning: read_care_save_warning(res.error)
      });
    } catch (e) {
      if (e.stage === STAGE_UPLOAD) {
        on_error(
          care_api_error(e.cause, 'The screenshot could not be uploaded. Nothing was changed.')
            .message
        );
      } else {
        screenshot_key.current = e.screenshot_key || '';
        // a 409 PAY_TIME_WARNING shows the acknowledgement step instead of an error
        if (!pw.catch_warning(e.cause)) on_error(swap_error_message(e.cause));
      }
    } finally {
      in_flight.current = false;
      setBusy(false);
    }
  };

  // ack: resend the same record (same uuid) with the acknowledgement from the warning step (earlier acks kept)
  const retry_save = (ack = null) => {
    const payload = with_care_ack(retry.payload, ack);
    setRetry({ ...retry, payload, busy: true });
    save_care_request(payload)
      .then(() => on_done())
      .catch((e) =>
        setRetry({
          ...retry,
          payload,
          busy: false,
          message: care_api_error(e, 'Try again.').message,
          warning: read_care_save_warning(e)
        })
      );
  };

  if (rt.warning && !retry) {
    return (
      <div>
        <h2>Change teacher</h2>
        <RequestTimeWarningStep
          warning={rt.warning}
          requestedLocal={draft ? draft.requested_at : ''}
          busy={busy}
          onAcknowledge={(ack) => {
            rt_ack.current = ack;
            rt.clear();
            submit();
          }}
          onChangeTime={rt.clear}
        />
      </div>
    );
  }

  if (pw.warning && !retry) {
    return (
      <div>
        <h2>Change teacher</h2>
        <PayTimeWarningStep
          warning={pw.warning}
          busy={busy}
          onAcknowledge={(ack) => submit(ack)}
          onChange={() => {
            // back to the form: a changed request time is checked again
            rt_ack.current = null;
            pw.clear();
          }}
        />
      </div>
    );
  }

  return (
    <div>
      <h2>Change teacher</h2>
      <CustomSingleSelect
        label="Select a teacher"
        options={teacher_options}
        onChange={(e) => {
          setTeacher(e.target.value);
          setErrors({});
        }}
        value={teacher_uuid}
      />
      {errors.teacher ? <p className="myt-form-error">* {errors.teacher}</p> : null}
      <RequiredReasonTextarea
        value={reason}
        onChange={(e) => {
          setReason(e.target.value);
          setErrors({});
        }}
        label={INTERNAL_NOTE_LABEL}
        placeholder={INTERNAL_NOTE_PLACEHOLDER}
        error={!!errors.reason}
        errorText={errors.reason}
        height="60px"
      />
      {draft ? (
        <TeacherNoteField
          value={draft.teacher_note || ''}
          onChange={(teacher_note) => {
            setDraft({ ...draft, teacher_note });
            setErrors({});
          }}
          error={errors.teacher_note}
        />
      ) : null}
      {draft ? (
        <CareRequestFields
          draft={draft}
          errors={errors}
          onChange={(patch) => {
            setDraft({ ...draft, ...patch });
            setErrors({});
            if ('file' in patch) screenshot_key.current = ''; // a new file must be uploaded again
          }}
          initiatedByFixed="TEACHER"
          requestedLabel="Teacher request time (IST)"
          bookedAt={booked_at}
        />
      ) : null}
      {retry ? (
        <CareSaveRetry
          message={retry.message}
          busy={retry.busy}
          warning={retry.warning}
          requestedAt={retry.payload.requested_at}
          onRetry={retry_save}
          onClose={on_done}
        />
      ) : (
        <FormActions>
          <Button onClick={() => submit()} disabled={busy}>
            {busy ? 'Submitting…' : 'Change'}
          </Button>
        </FormActions>
      )}
    </div>
  );
}

TeacherChangeForm.propTypes = {
  session_uuid: PropTypes.string.isRequired,
  session_type: PropTypes.string,
  teacher_options: PropTypes.arrayOf(
    PropTypes.shape({ value: PropTypes.string, label: PropTypes.string })
  ).isRequired,
  initial_teacher_uuid: PropTypes.string,
  swap: PropTypes.func.isRequired,
  on_done: PropTypes.func.isRequired,
  on_error: PropTypes.func.isRequired,
  // the class start (epoch ms) and booking time, for the request-time pre-check (A-52)
  class_start: PropTypes.number,
  booked_at: PropTypes.string
};

TeacherChangeForm.defaultProps = {
  session_type: '',
  initial_teacher_uuid: '',
  class_start: undefined,
  booked_at: ''
};

export default TeacherChangeForm;
